import { Injectable, OnDestroy } from '@angular/core';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import { BehaviorSubject, Observable } from 'rxjs';
import { WebsocketConfigService } from './websocket-config.service';

export type WebSocketConnectionState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface StompMessage {
  body: string;
  headers: Record<string, string>;
}

type StompMessageHandler = (message: StompMessage) => void;
type StompConnectHandler = () => void;

interface TopicSubscription {
  id: string;
  topic: string;
  handler: StompMessageHandler;
  stompSub?: StompSubscription;
}

@Injectable({
  providedIn: 'root',
})
export class WebsocketService implements OnDestroy {
  private client: Client | null = null;
  private intentionalClose = false;
  private reconnectAttempts = 0;
  private nextSubscriptionId = 0;
  private readonly topicSubscriptions: TopicSubscription[] = [];
  private readonly onConnectHandlers: StompConnectHandler[] = [];

  private readonly stateSubject = new BehaviorSubject<WebSocketConnectionState>('idle');

  constructor(private readonly wsConfig: WebsocketConfigService) {}

  ngOnDestroy(): void {
    this.disconnect();
  }

  get connectionState$(): Observable<WebSocketConnectionState> {
    return this.stateSubject.asObservable();
  }

  get isConnected(): boolean {
    return !!this.client?.connected;
  }

  connect(): void {
    if (!this.wsConfig.isEnabled()) {
      return;
    }

    if (this.client?.active) {
      return;
    }

    this.intentionalClose = false;
    this.stateSubject.next('connecting');
    this.client = this.buildClient(this.wsConfig.getConnectionUrl());
    this.client.activate();
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.reconnectAttempts = 0;
    this.clearStompSubscriptions();
    this.client?.deactivate();
    this.client = null;
    this.stateSubject.next('idle');
  }

  subscribe(topic: string, handler: StompMessageHandler): () => void {
    const subscription: TopicSubscription = {
      id: `sub-${this.nextSubscriptionId++}`,
      topic,
      handler,
    };
    this.topicSubscriptions.push(subscription);
    this.attachTopicSubscription(subscription);

    return () => {
      const index = this.topicSubscriptions.indexOf(subscription);
      if (index >= 0) {
        subscription.stompSub?.unsubscribe();
        this.topicSubscriptions.splice(index, 1);
      }
    };
  }

  onConnect(handler: StompConnectHandler): () => void {
    this.onConnectHandlers.push(handler);
    if (this.client?.connected) {
      handler();
    }

    return () => {
      const index = this.onConnectHandlers.indexOf(handler);
      if (index >= 0) {
        this.onConnectHandlers.splice(index, 1);
      }
    };
  }

  publish(options: { destination: string; body?: string }): void {
    if (!this.client?.connected) {
      return;
    }

    this.client.publish({
      destination: options.destination,
      body: options.body ?? '',
    });
  }

  private buildClient(brokerURL: string): Client {
    const heartbeat = this.wsConfig.getHeartbeatIntervalMs();
    const client = new Client({
      brokerURL,
      connectHeaders: this.buildConnectHeaders(),
      reconnectDelay: this.wsConfig.getReconnectDelayMs(),
      heartbeatIncoming: heartbeat,
      heartbeatOutgoing: heartbeat,
      beforeConnect: async () => {
        const sockJsBase = this.wsConfig.getSockJsHttpBase();
        if (sockJsBase.includes('ws-sockjs')) {
          const brokerURL = await this.wsConfig.resolveSockJsWebSocketUrl();
          if (brokerURL) {
            client.brokerURL = brokerURL;
          }
        }
        client.connectHeaders = this.buildConnectHeaders();
      },
      onConnect: () => {
        this.reconnectAttempts = 0;
        this.stateSubject.next('open');
        this.subscribeAllTopics();
        this.onConnectHandlers.forEach((handler) => handler());
      },
      onStompError: (frame) => {
        console.error('[WebSocket] STOMP error:', frame.headers, frame.body);
        this.stateSubject.next('error');
      },
      onWebSocketError: () => {
        this.stateSubject.next('error');
      },
      onWebSocketClose: () => {
        this.clearStompSubscriptions();
        this.stateSubject.next('closed');

        if (this.intentionalClose || !this.wsConfig.isEnabled()) {
          return;
        }

        const maxAttempts = this.wsConfig.getMaxReconnectAttempts();
        if (this.reconnectAttempts >= maxAttempts) {
          console.warn('[WebSocket] Max reconnect attempts reached');
          return;
        }

        this.reconnectAttempts++;
      },
    });

    return client;
  }

  private buildConnectHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    const token = this.wsConfig.getAuthTokenQueryValue();

    if (token) {
      // Match app HTTP interceptor: raw token in Authorization (not Bearer).
      headers['Authorization'] = token;
      headers['token'] = token;
    }

    return headers;
  }

  private attachTopicSubscription(subscription: TopicSubscription): void {
    if (!this.client?.connected) {
      return;
    }

    subscription.stompSub?.unsubscribe();
    subscription.stompSub = this.client.subscribe(subscription.topic, (message: IMessage) => {
      this.dispatchMessage(subscription.topic, message);
    });
  }

  private subscribeAllTopics(): void {
    for (const subscription of this.topicSubscriptions) {
      this.attachTopicSubscription(subscription);
    }
  }

  private clearStompSubscriptions(): void {
    for (const subscription of this.topicSubscriptions) {
      subscription.stompSub?.unsubscribe();
      subscription.stompSub = undefined;
    }
  }

  private dispatchMessage(destination: string, message: IMessage): void {
    const stompMessage: StompMessage = {
      body: message.body,
      headers: { ...(message.headers as Record<string, string>) },
    };

    console.log('[WebSocket] MESSAGE received:', destination, stompMessage.body);

    let handled = false;
    for (const sub of this.topicSubscriptions) {
      if (sub.topic === destination) {
        sub.handler(stompMessage);
        handled = true;
      }
    }

    if (!handled) {
      console.log('[WebSocket] MESSAGE (no subscriber):', destination, stompMessage.body);
    }
  }
}
